import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { GetSleepScreenAssignmentCounts } from '../../libs/GraphQL';
import styles from './EditCascadeWarningModal.module.css';

const fridgeWord = (n) => (n === 1 ? 'fridge' : 'fridges');

const EditCascadeWarningModal = ({
  show,
  onCancel,
  onConfirm,
  sleepScreenId,
  descriptionBefore,
  descriptionAfter,
  videoUrlBefore,
  videoUrlAfter,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [count, setCount] = useState(null);
  const [countError, setCountError] = useState(false);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setCount(null);
    setCountError(false);
    const fetchCount = async () => {
      try {
        const rows = await GetSleepScreenAssignmentCounts();
        if (cancelled) return;
        const match = (rows || []).find(
          (r) => r.sleepScreenId === sleepScreenId,
        );
        setCount(match ? match.count : 0);
      } catch (err) {
        if (cancelled) return;
        console.error('getSleepScreenAssignmentCounts failed:', err);
        setCountError(true);
      }
    };
    fetchCount();
    return () => {
      cancelled = true;
    };
  }, [show, sleepScreenId]);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };

  const handleHide = () => {
    if (submitting) return;
    onCancel();
  };

  const descriptionChanged = descriptionBefore !== descriptionAfter;
  const videoUrlChanged = videoUrlBefore !== videoUrlAfter;

  const countLoading = count === null && !countError;
  const confirmDisabled = submitting || countLoading || countError;

  const confirmLabel = (() => {
    if (submitting) return 'Saving…';
    if (countLoading) return 'Checking…';
    if (countError) return 'Retry to enable save';
    if (count === 0) return 'Yes, save changes';
    return `Yes, save & update ${count.toLocaleString()} ${fridgeWord(count)}`;
  })();

  return (
    <Modal show={show} onHide={handleHide} backdrop="static" centered>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Save changes to this entry?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.calloutRow}>
          <div className={styles.calloutNumber}>
            {countLoading && <span className={styles.spinner} />}
            {countError && '!'}
            {count !== null && count.toLocaleString()}
          </div>
          <div className={styles.calloutText}>
            {countLoading && <>Checking how many fridges this affects…</>}
            {countError && (
              <>
                <strong>Couldn&apos;t fetch the assignment count.</strong>{' '}
                Cancel and reopen to try again — we can&apos;t safely confirm
                the cascade impact until this loads.
              </>
            )}
            {count !== null && count === 0 && (
              <>
                No fridges are currently assigned to this entry. Saving will
                update the library record only.
              </>
            )}
            {count !== null && count > 0 && (
              <>
                Saving will update <strong>{count.toLocaleString()}</strong>{' '}
                {fridgeWord(count)} currently assigned to this entry. The new
                video will start playing within ~15 minutes.
              </>
            )}
          </div>
        </div>

        <div className={styles.changesLabel}>Changes</div>
        <div className={styles.diffBlock}>
          {descriptionChanged && (
            <div className={styles.diffRow}>
              <div className={styles.diffField}>Description</div>
              <div className={styles.diffValues}>
                <div className={styles.diffBefore}>
                  {descriptionBefore || <em>(empty)</em>}
                </div>
                <div className={styles.diffAfter}>
                  {descriptionAfter || <em>(empty)</em>}
                </div>
              </div>
            </div>
          )}
          {videoUrlChanged && (
            <div className={styles.diffRow}>
              <div className={styles.diffField}>Video URL</div>
              <div className={styles.diffValues}>
                <div className={`${styles.diffBefore} ${styles.mono}`}>
                  {videoUrlBefore || <em>(empty)</em>}
                </div>
                <div className={`${styles.diffAfter} ${styles.mono}`}>
                  {videoUrlAfter || <em>(empty)</em>}
                </div>
              </div>
            </div>
          )}
        </div>

        <p className={styles.helper}>
          Need to change which fridges this plays on? Cancel and use the
          assignment flow instead.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleHide}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

EditCascadeWarningModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  sleepScreenId: PropTypes.string.isRequired,
  descriptionBefore: PropTypes.string,
  descriptionAfter: PropTypes.string,
  videoUrlBefore: PropTypes.string,
  videoUrlAfter: PropTypes.string,
};

EditCascadeWarningModal.defaultProps = {
  descriptionBefore: '',
  descriptionAfter: '',
  videoUrlBefore: '',
  videoUrlAfter: '',
};

export default EditCascadeWarningModal;
